import type { GettextInterface } from '@indeed/indeed-gettext';
import type { GetAutoApplyReviewDataQuery } from './__generated__/ReviewPage.generated';

export const getLocation = (
    resumeLocation: NonNullable<
        NonNullable<GetAutoApplyReviewDataQuery['jobSeekerProfile']>['profile']
    >['resume'][number]['location'],
    defaultContactInfoLocation: NonNullable<
        NonNullable<
            NonNullable<
                NonNullable<GetAutoApplyReviewDataQuery['jobSeekerProfile']>['profile']
            >['defaultInfo']
        >['contactInformation']
    >['location'],
) => {
    return resumeLocation || defaultContactInfoLocation;
};

export const getCityState = (
    resumeLocation: NonNullable<
        NonNullable<GetAutoApplyReviewDataQuery['jobSeekerProfile']>['profile']
    >['resume'][number]['location'],
    defaultContactInfoLocation: NonNullable<
        NonNullable<
            NonNullable<
                NonNullable<GetAutoApplyReviewDataQuery['jobSeekerProfile']>['profile']
            >['defaultInfo']
        >['contactInformation']
    >['location'],
): string | undefined => {
    const location = getLocation(resumeLocation, defaultContactInfoLocation);
    if (!location) {
        return undefined;
    }
    if (location.formattedLocation) {
        return location.formattedLocation;
    }
    if (location.city && location.admin1) {
        return `${location.city}, ${location.admin1}`;
    }
    return location.city ?? location.admin1 ?? undefined;
};

export const getFullName = (
    firstName: string | null | undefined,
    lastName: string | null | undefined,
    gt: GettextInterface,
): string | undefined => {
    if (!!firstName && !!lastName) {
        return gt.pgettext('full name, {0} is first name and {1} is last name', '{0} {1}', [
            firstName,
            lastName,
        ]);
    }
    return firstName ?? lastName ?? undefined;
};

export type ContactInformationData = {
    fullName: string | undefined;
    phoneNumber: string | null | undefined;
    country: string | null | undefined;
    cityState: string | undefined;
    address: string | null | undefined;
    emailAddress: string | undefined;
    showContactInfo: boolean;
};

/**
 * This mirrors profile-one-frontend
 */
export const getContactInfo = (
    profile: NonNullable<GetAutoApplyReviewDataQuery['jobSeekerProfile']>['profile'],
    accountProfile: NonNullable<
        NonNullable<GetAutoApplyReviewDataQuery['currentUser']>['account']
    >['accountProfile'],
    isContactInfoEnabled: boolean,
    gt: GettextInterface,
): ContactInformationData => {
    const resumeData = profile?.resume[0];
    const defaultContactInfo = profile?.defaultInfo?.contactInformation;
    const fullName = getFullName(
        resumeData?.firstName || defaultContactInfo?.firstName,
        resumeData?.lastName || defaultContactInfo?.lastName,
        gt,
    );
    const phoneNumber = resumeData?.phoneNumber ?? defaultContactInfo?.phoneNumber;
    const cityState = getCityState(resumeData?.location, defaultContactInfo?.location);
    const location = getLocation(resumeData?.location, defaultContactInfo?.location);
    const address = location?.address;
    const country = location?.country;
    const emailAddress = accountProfile?.email?.emailAddress;
    const showContactInfo =
        isContactInfoEnabled &&
        !!(fullName || phoneNumber || country || cityState || address || emailAddress);
    return { fullName, phoneNumber, country, cityState, address, emailAddress, showContactInfo };
};
